import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Header from './Header'
import './LandingPage.css'

const LandingPage = () => {
  const portfolioRef = useRef<HTMLDivElement>(null)
  const [isPortfolioVisible, setIsPortfolioVisible] = useState(false)
  const [showBoldText, setShowBoldText] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [currentTestimonialSlide, setCurrentTestimonialSlide] = useState(0)
  const [itemsPerView, setItemsPerView] = useState(4)
  const sliderImages = [
    "/Slider%20images-01.png",
    "/Slider%20images-02.png",
    "/Slider%20images-03.png",
    "/Slider%20images-04.png",
    "/Slider%20images-05.png",
    "/Slider%20images-06.png",
    "/Slider%20images-07.png",
    "/Slider%20images-08.png"
  ]

  const testimonials = [
    { name: "NEUVIAN SKINCARE", headline: "BOLD Creative is a standout when it comes to custom artwork.", text: "Their designs are not only visually stunning but also perfectly aligned with your brand identity. Whether you are looking for sleek, minimal designs or something more vibrant, they have the creativity and expertise to bring your vision to life.", icon: "/WEBSITE IMAGES-23.svg", color: "purple" },
    { name: "Dr AYESHA KANWAL SKIN AESTHETICS", headline: "I love how kind, cooperative, and accessible you all are.", text: "Working with you feels so smooth and stress-free, and I truly value the support you provide, not just with social media but even beyond, like helping with my clinic interior decor. It really means a lot!", icon: "/WEBSITE IMAGES-22.svg", color: "green" },
    { name: "ARTHYMIS", headline: "I had an absolutely wonderful & flawless experience working with Bold CS.", text: "Adore all the brand work, services, Artwork stickers are too cute, & just overall the attention to detail, the flexibility, the communication, the color palette, the fonts everything was so well thought of! Extremely happy - can't wait to work again already on future projects & def recommending to my fellow entrepreneurs.", icon: "/WEBSITE IMAGES-21.svg", color: "blue", decorative: true }
  ]  // Scroll animation for portfolio section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsPortfolioVisible(true)
          }
        })
      },
      {
        threshold: 0.2, // Trigger when 20% of the section is visible
        rootMargin: '0px 0px -100px 0px'
      }
    )

    if (portfolioRef.current) {
      observer.observe(portfolioRef.current)
    }

    return () => {
      if (portfolioRef.current) {
        observer.unobserve(portfolioRef.current)
      }
    }
  }, [])

  // Scroll event listener to show "WE DO BOLD" text
  useEffect(() => {
    const handleScroll = () => {
      if (!portfolioRef.current) return;
      const rect = portfolioRef.current.getBoundingClientRect();
      // Switch to "WE DO BOLD" when user scrolls down
      // Since portfolio section is near the top, we want to trigger when rect.top is negative
      if (rect.top < 0) {
        setShowBoldText(true)
        setIsPortfolioVisible(true)
      } else {
        setShowBoldText(false)
        setIsPortfolioVisible(true) // Always visible initially
      }
    }

    window.addEventListener('scroll', handleScroll)
    // Initial check
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Handle responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setItemsPerView(1)
      } else if (window.innerWidth <= 768) {
        setItemsPerView(2)
      } else {
        setItemsPerView(4)
      }
    }
    
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const maxSlide = Math.max(0, sliderImages.length - itemsPerView)
        if (prev >= maxSlide) {
          return 0 // Loop back to the beginning
        }
        return prev + 1
      })
    }, 3000) // Change slide every 3 seconds

    return () => clearInterval(interval)
  }, [sliderImages.length, itemsPerView])

  const goToSlide = (index: number) => {
    const maxSlide = Math.max(0, sliderImages.length - itemsPerView)
    setCurrentSlide(Math.min(index, maxSlide))
  }
  return (
    <div className="landing-page">
      <Header />

      {/* Portfolio Section */}
      <section ref={portfolioRef} id="portfolio" className="portfolio-section">
        <div className="portfolio-wavy-top" style={{ display: 'none' }}>
          {/* Wave 1 - Green (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-green-1">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#CDD951" />
          </svg>
          {/* Wave 2 - White (downward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-white">
            <path d="M0,50 Q60,80 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#ffffff" />
          </svg>
          {/* Wave 3 - Green (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-green-2">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#CDD951" />
          </svg>
        </div>
        {/* New wave for top edge below header */}
        <div style={{ position: 'absolute', top: '-1px', left: 0, width: '100%', height: '80px', zIndex: 1, lineHeight: 0, pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%', transform: 'rotate(180deg)', display: 'block' }}>
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="portfolio-right-icon">
          <img src="/icons BCS-22.svg" alt="Pink star burst" className="portfolio-icon" />
        </div>
        <div className="portfolio-spark-left">
          <img src="/icons BCS-22.svg" alt="Spark" className="portfolio-spark" />
        </div>
        <div className="portfolio-spark-right">
          <img src="/icons BCS-22.svg" alt="Spark" className="portfolio-spark" />
        </div>
        <div className="portfolio-speech-bubble">
          <img src="/icons BCS-35.svg" alt="Speech bubble" className="portfolio-speech-icon" />
        </div>
        <div className="portfolio-content">
          <div className={`portfolio-text ${isPortfolioVisible ? 'portfolio-text-visible' : ''} ${showBoldText ? 'portfolio-text-swapped' : ''}`}>
            <h2 className="portfolio-headline-1">WE DON'T DO<br />ORDINARY</h2>
            <h2 className="portfolio-headline-2">WE DO<br />BOLD</h2>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="services-content">
          <h2 className="services-headline">UNFORGETTABLE BRAND IDENTITIES</h2>
          <p className="services-subheadline">for bold businesses that dare to be different</p>
          <p className="services-description">From strategy to social — we craft identities and content that break boundaries.</p>
          <Link to="/contact" className="services-button">WORK WITH US</Link>
        </div>
      </section>

      {/* How We Build Section */}
      <section className="how-we-build-section">
        <div className="how-we-build-banner">
          <div className="how-we-build-mouse">
            <img src="/icons%20BCS-21.svg" alt="Computer mouse" className="mouse-icon" />
          </div>
          <div className="how-we-build-content">
            <h2 className="how-we-build-headline">
              HOW WE BUILD <span className="fearless-text">FEARLESS</span> BRANDS
            </h2>
            <p className="how-we-build-tagline">Let's talk strategy, story, and standout design — all tailored to you.</p>
          </div>
        </div>
      </section>

      {/* Portfolio Showcase Section */}
      <section className="portfolio-showcase-section">
        <div className="portfolio-showcase-grid">
          <div className="portfolio-showcase-card">
            <div className="portfolio-showcase-image">
              <img src="/IMAGE-10.png" alt="Corporate Branding" loading="lazy" />
            </div>
            <div className="portfolio-showcase-label">CORPORATE BRANDING & DESIGN</div>
          </div>
          <div className="portfolio-showcase-card">
            <div className="portfolio-showcase-image">
              <img src="/IMAGE-11.png" alt="Brand Development" loading="lazy" />
            </div>
            <div className="portfolio-showcase-label">BRAND DEVELOPMENT & VISUAL IDENTITY</div>
          </div>
          <div className="portfolio-showcase-card">
            <div className="portfolio-showcase-image">
              <img src="/IMAGE-12.png" alt="Packaging Design" loading="lazy" />
            </div>
            <div className="portfolio-showcase-label">PACKAGING DESIGN</div>
          </div>
        </div>
        <div className="portfolio-showcase-button-container">
          <Link to="/services" className="portfolio-showcase-button">EXPLORE SERVICES</Link>
        </div>
      </section>

      {/* New Section After Explore Services */}
      <section className="new-section-after-services">
        <div className="new-section-icon">
          <img src="/icons%20BCS-23.svg" alt="Sunglasses icon" className="sunglasses-icon" />
        </div>
        <div className="new-section-content">
          <p className="new-section-script">We've Built Brands That</p>
          <h2 className="new-section-headline">DON'T BLEND IN — THEY BREAK THROUGH.</h2>
          <p className="new-section-description">From beauty to tech – every identity we craft tells a story, not just a logo.</p>
        </div>
      </section>

      {/* Portfolio Showcase Section */}
      <section className="portfolio-showcase-main-section">
        <div className="portfolio-showcase-wavy-top">
          {/* Wave 1 - Green (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-green-1">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#CDD951" />
          </svg>
          {/* Wave 2 - White (downward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-white" style={{ display: 'none' }}>
            <path d="M0,50 Q60,80 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#ffffff" />
          </svg>
          {/* Wave 3 - Green (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-green-2">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#CDD951" />
          </svg>
        </div>
        <div className="portfolio-showcase-main-container">
          <div className="portfolio-slider-wrapper">
            <div className="portfolio-slider-container">
              <div className="portfolio-slider-track" style={{ transform: `translateX(-${currentSlide * (100 / itemsPerView)}%)` }}>
                {sliderImages.map((src, index) => (
                  <div key={index} className="portfolio-slider-slide">
                    <div className="portfolio-showcase-main-item">
                      <div className="portfolio-showcase-main-image">
                        <img src={src} alt={`Portfolio image ${index + 1}`} loading="lazy" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="portfolio-slider-dots">
              {Array.from({ length: Math.max(1, sliderImages.length - itemsPerView + 1) }, (_, index) => (
                <button
                  key={index}
                  className={`portfolio-slider-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="portfolio-showcase-main-button-container">
            <Link to="/portfolio" className="portfolio-showcase-main-button">EXPLORE PORTFOLIO</Link>
          </div>
        </div>
      </section>

      {/* Bold Ideas Banner Section */}
      <section className="bold-ideas-banner-section">
        <div className="bold-ideas-banner-icon">
          <img src="/icons%20BCS-25.svg" alt="Megaphone icon" className="bold-ideas-megaphone-icon" />
        </div>
        <div className="bold-ideas-banner-container">
          <div className="bold-ideas-banner-track">
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            {/* Duplicate items for seamless loop */}
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
            <div className="bold-ideas-banner-item">
              <img src="/submark%20socials@4x.png" alt="Bold Ideas Logo" className="bold-ideas-logo" />
              <p className="bold-ideas-text">Turning Bold Ideas Into Reality</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bold Ideas Showcase Section */}
      <section id="about" className="bold-ideas-showcase-section">
        <div className="bold-ideas-star-top-right">
          <img src="/icons BCS-22.svg" alt="Star icon" className="bold-ideas-star-icon" />
        </div>
        <div className="bold-ideas-star-bottom-left">
          <img src="/icons BCS-22.svg" alt="Star icon" className="bold-ideas-star-icon" />
        </div>
        <div className="bold-ideas-showcase-container">
          <div className="bold-ideas-content-left">
            <h2 className="bold-ideas-heading">
              <span className="bold-ideas-heading-purple">BRINGING BOLD IDEAS,</span>
              <span className="bold-ideas-heading-purple">VISION, AND VIBE TO</span>
              <span className="bold-ideas-heading-green">YOUR BRAND</span>
            </h2>
            <p className="bold-ideas-description">
              Hey, we're BOLD Creative Studios! We work with brands and businesses who want to stand out. From strategy to visuals, we craft brands that tell stories, turn heads, and leave a mark. Every project is a playground for fierce ideas, fearless creativity, and designs that truly connect.
            </p>
            <Link to="/about" className="bold-ideas-button">Get to Know Us</Link>
          </div>
          <div className="bold-ideas-image-container">
            <img src="/Posters_on_Fence_Mockup_2.jpg" alt="Bold Ideas Showcase" className="bold-ideas-showcase-image" />
          </div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="process-steps-section">
        <div className="process-steps-content">
          <p className="process-steps-script">Lets Break It Down</p>
          <div className="process-steps-title-container">
            <h2 className="process-steps-headline">OUR BOLD WAY OF WORKING TOGETHER</h2>
            <div className="process-steps-triangle-icon">
              <img src="/icons BCS-18.svg" alt="" className="process-triangle-img" />
            </div>
          </div>
          <p className="process-steps-tagline">Here's the bold, clear-cut roadmap that takes your brand from 'just an idea' to 'unforgettable impact.'</p>

          <div className="process-steps-container">
            <div className="process-steps-dashed-lines">
              <img src="/doted lines-02.svg" alt="" className="process-dots-img" />
            </div>

            <div className="process-step-box">
              <div className="process-step-puzzle-icon">
                <img src="/icons BCS-42.svg" alt="" />
              </div>
              <h3 className="process-step-number">STEP 1:</h3>
              <h4 className="process-step-title">ENQUIRY</h4>
              <p className="process-step-description">Fill out our enquiry form (available on our IG story highlights) or simply slide into our DMs!</p>
            </div>

            <div className="process-step-box">
              <h3 className="process-step-number">STEP 2:</h3>
              <h4 className="process-step-title"></h4>
              <p className="process-step-description">You'll get a detailed questionnaire with 25 thoughtful questions about your brand's mission, vision, and design preferences.</p>
            </div>

            <div className="process-step-box">
              <h3 className="process-step-number">STEP 3:</h3>
              <h4 className="process-step-title">STRATEGY & CREATIVE DIRECTION</h4>
              <p className="process-step-description">Using your responses + in-depth market research, we craft a brand strategy & creative direction. This includes two moodboards for you to review and approve.</p>
            </div>
          </div>

          <div className="process-steps-button-container">
            <Link to="/our-process" className="process-steps-button">SEE THE PROCESS</Link>
          </div>

          {/* Final CTA Section */}
          <div className="final-cta-process-container">
            <div className="final-cta-process-content">
              <div className="final-cta-process-text-container">
                <p className="final-cta-process-text-line1">Stop blending in — start branding out loud.</p>
                <p className="final-cta-process-text-line2">Whether you're launching, rebranding, or scaling your presence — let's build something unforgettable together.</p>
                <p className="final-cta-process-text-line3">From Idea → Identity → Impact</p>
              </div>
              <div className="final-cta-process-button-container">
                <Link to="/contact" className="final-cta-process-button">ENQUIRE</Link>
              </div>
            </div>
            <div className="final-cta-process-handshake">
              <div className="final-cta-process-handshake-icon">
                <img src="/icons%20BCS-20.svg" alt="Handshake icon" className="final-cta-handshake-icon-img" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials Section */}
      <section className="client-testimonials-section">
        <div className="client-testimonials-wavy-top">
          {/* Wave 1 - White (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-white-1">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#ffffff" />
          </svg>
          {/* Wave 2 - Green (downward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="-120 0 1680 100" preserveAspectRatio="none" className="wavy-svg wavy-green-testimonials">
            <path d="M-120,50 Q-60,80 0,50 T120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 T1560,50 L1560,100 L-120,100 Z" fill="#CDD951" />
          </svg>
          {/* Wave 3 - White (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-white-2">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="client-testimonials-speech-icon">
          <img src="/icons%20BCS-35.svg" alt="Speech bubble icon" className="client-testimonials-speech-bubble" />
        </div>
        <div className="client-testimonials-content">
          <h2 className="client-testimonials-title">CLIENT CHEERS</h2>
          <p className="client-testimonials-subtitle">Proof that bold ideas work, create impact, and leave a mark.</p>

          <div className="client-testimonials-carousel" style={{ overflow: 'hidden', paddingTop: '60px' }}>
            <button 
              className="client-testimonials-arrow client-testimonials-arrow-left" 
              aria-label="Previous testimonial"
              onClick={() => setCurrentTestimonialSlide(prev => Math.max(0, prev - 1))}
              disabled={currentTestimonialSlide === 0}
              style={{ opacity: currentTestimonialSlide === 0 ? 0.5 : 1, cursor: currentTestimonialSlide === 0 ? 'default' : 'pointer' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="#594F9A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <div 
              className="client-testimonials-cards"
              style={{ 
                '--slide': currentTestimonialSlide,
                transform: `translateX(calc(-${currentTestimonialSlide * 100}% - ${currentTestimonialSlide * 30}px))`,
                transition: 'transform 0.5s ease-in-out'
              } as React.CSSProperties}
            >
              {testimonials.map((testimonial, idx) => (
                <div className="client-testimonial-card" key={idx}>
                  <div className="client-testimonial-icon">
                    <div className={`client-icon-circle client-icon-${testimonial.color}`}>
                      <img src={testimonial.icon} alt={`${testimonial.name} icon`} className="client-icon-image" />
                    </div>
                  </div>
                  <h3 className="client-testimonial-headline">{testimonial.headline}</h3>
                  <p className="client-testimonial-text">{testimonial.text}</p>
                  <p className="client-testimonial-name">{testimonial.name}</p>
                  {testimonial.decorative && (
                    <div className="client-testimonial-decorative-icon">
                      <img src="/icons%20BCS-39.svg" alt="Decorative icon" className="arthymis-decorative-icon" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button 
              className="client-testimonials-arrow client-testimonials-arrow-right" 
              aria-label="Next testimonial"
              onClick={() => setCurrentTestimonialSlide(prev => Math.min(testimonials.length - 1, prev + 1))}
              disabled={currentTestimonialSlide === testimonials.length - 1}
              style={{ opacity: currentTestimonialSlide === testimonials.length - 1 ? 0.5 : 1, cursor: currentTestimonialSlide === testimonials.length - 1 ? 'default' : 'pointer' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="#594F9A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className="client-testimonials-dots-container">
            <div className="client-testimonials-pagination-dots">
              {testimonials.map((_, idx) => (
                <span 
                  key={idx} 
                  className={`testimonial-dot ${currentTestimonialSlide === idx ? 'active' : ''}`}
                  onClick={() => setCurrentTestimonialSlide(idx)}
                  style={{ cursor: 'pointer' }}
                ></span>
              ))}
            </div>
          </div>


        </div>
      </section>

      {/* Dare to Stand Out Section */}
      <section className="dare-stand-out-section">
        <div className="dare-stand-out-top-left-icon">
          <img src="/icons%20BCS-40.svg" alt="Decorative icon" className="dare-stand-out-top-left-icon-img" />
        </div>
        <div className="dare-stand-out-heart-cover"></div>
        <div className="dare-stand-out-content">
          <h2 className="dare-stand-out-headline">DARE TO STAND OUT</h2>
          <p className="dare-stand-out-description">Designing unforgettable brand identities for businesses that break the mold.</p>
          <Link to="/contact" className="dare-stand-out-button">START YOUR PROJECT</Link>
        </div>
        <div className="dare-stand-out-led-icon">
          <img src="/icons%20BCS-17.svg" alt="LED Monitor icon" className="dare-stand-out-led-icon-img" />
        </div>
      </section>

      {/* Footer Section */}
      <footer className="footer-section">
        <div className="footer-wavy-top">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-green-2">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#CDD951" />
          </svg>
        </div>
        <div className="footer-star-icon">
          <img src="/icons BCS-44.svg" alt="Star icon" className="footer-star-icon-img" />
        </div>
        <div className="footer-content">
          <div className="footer-logo">
            <img src="/BCS%20LOGO-01%20(1).png" alt="BOLD! CREATIVE STUDIO" className="footer-logo-img" />
          </div>
          <div className="footer-social-icons">
            <a href="https://www.instagram.com/brandingoutloud/" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="Instagram">
              <img src="/WEBSITE IMAGES-17.svg" alt="Instagram" className="footer-social-icon-img" />
            </a>
            <a href="https://www.behance.net/brandingoutloud?utm_source=ig&utm_medium=social&utm_content=link_in_bio&fbclid=PAZXh0bgNhZW0CMTEAc3J0YwZhcHBfaWQMMjU2MjgxMDQwNTU4AAGnHFYMm_3Cc_qOoIHo5M7pIpJVh1fw7yG_MjUWcetV-lC7sxbyqLSp49vJ22Y_aem_NaED6HYtWH8rqyuMagDYig" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="Behance">
              <img src="/WEBSITE IMAGES-18.svg" alt="Behance" className="footer-social-icon-img" />
            </a>
            <a href="https://www.linkedin.com/company/brandingoutloud/" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="LinkedIn">
              <div className="footer-social-icon-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#594F9A', borderRadius: '50%', transform: 'scale(0.75)' }}>
                <svg viewBox="0 0 24 24" fill="white" style={{ width: '24px', height: '24px', margin: 0 }}>
                  <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z"/>
                </svg>
              </div>
            </a>
            <a href="https://www.facebook.com/share/1879QvWrk8/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="Facebook">
              <img src="/WEBSITE IMAGES-19.svg" alt="Facebook" className="footer-social-icon-img" />
            </a>
          </div>
        </div>
      </footer>

    </div>
  )
}

export default LandingPage
